import clsx from 'clsx'
import FigureValue from '@/components/dashboards/FigureValue'
import { BasisNote } from '@/components/dashboards/KnownFigures'
import {
  MEASURE_BY_KEY,
  MEASURE_SUBSET,
  formatMeasure,
  measureFigure,
  type DealFact,
  type HeadlineMeasures,
  type MeasureKey,
} from '@/services/dashboardAnalytics'

/** The headline set, in reading order. Quote rate leads the rates (T-71). */
export const HEADLINE_MEASURES: MeasureKey[] = [
  'count',
  'commitment',
  'fundedCount',
  'funded',
  'quoteRatePct',
  'quoteToTermSheetPct',
  'wtdLtvLtc',
  'avgDaysToFund',
]

const LABEL_OVERRIDE: Partial<Record<MeasureKey, string>> = {
  count: 'Deals',
  commitment: 'Total commitment',
  funded: 'Funded volume',
  fundedCount: 'Funded deals',
  quoteRatePct: 'Quote rate',
  quoteToTermSheetPct: 'Quote → term sheet',
  conversionPct: 'Funded ÷ submitted',
  wtdLtvLtc: 'Wtd LTV / LTC',
  avgDaysToFund: 'Avg days to fund',
}

/** Ranks lower-is-better for these (the server's `_ASCENDING_MEASURES`). */
const ASCENDING = new Set<MeasureKey>(['avgDaysToFund'])

function rankTone(rank: number | null | undefined, peers: number | undefined): string {
  if (rank == null || !peers) return 'text-stone-500'
  if (rank === 1) return 'text-amber-200'
  if (rank <= Math.ceil(peers / 3)) return 'text-emerald-300/90'
  return 'text-stone-400'
}

interface PersonHeadlineProps {
  headline: HeadlineMeasures
  deals: readonly DealFact[]
  name: string
}

/**
 * The person's headline figures, each with where they rank among the peers
 * in the same role and the peer median beside it. Each value opens exactly
 * the deals it stands on (the funded deals for funded volume, the quoted
 * deals for the quote rate, the deals stating an LTV for the weighted LTV).
 */
export default function PersonHeadline({ headline, deals, name }: PersonHeadlineProps) {
  const facts = new Map(deals.map((deal) => [deal.id, deal]))
  const peers = headline.peers
  return (
    <div className="grid grid-cols-2 gap-1.5 md:grid-cols-4 min-[1900px]:grid-cols-8">
      {HEADLINE_MEASURES.map((key) => {
        const meta = MEASURE_BY_KEY[key]
        const predicate = MEASURE_SUBSET[key]
        const ids = predicate
          ? (headline.measures.dealIds ?? []).filter((id) => {
              const fact = facts.get(id)
              return fact ? predicate(fact) : false
            })
          : headline.measures.dealIds
        const figure = measureFigure(headline.measures, key, {
          ids,
          title: `${name} — ${LABEL_OVERRIDE[key] ?? meta.label}`,
          label: LABEL_OVERRIDE[key] ?? meta.label,
        })
        const rank = headline.rank?.[key]
        const peerMedian = headline.peerMedian?.[key]
        const display = meta.unit === 'days' && typeof figure.value === 'number' ? { ...figure, value: Math.round(figure.value) } : figure
        return (
          <div
            key={key}
            className="relative min-w-0 overflow-hidden rounded-md border border-amber-400/[0.09] bg-gradient-to-b from-[#121110] to-[#0a0a0a] px-2.5 py-2"
          >
            <div className="break-words font-mono text-[8.5px] uppercase tracking-[0.16em] text-stone-500" title={figure.label}>
              {figure.label}
            </div>
            <div className="mt-0.5 truncate text-[17px] font-semibold leading-tight tabular-nums text-stone-50">
              <FigureValue figure={display} />
              {meta.unit === 'days' && figure.value != null && <span className="ml-0.5 text-[11px] text-stone-500">d</span>}
            </div>
            {/* Only measures the server ranks carry a rank line; the rest
                (pull-through, weighted LTV) are not compared across peers. */}
            {headline.rank && key in headline.rank && (
            <div className="mt-1 flex items-baseline justify-between gap-1 font-mono text-[9px] tabular-nums">
              <span
                className={clsx(rankTone(rank, peers))}
                title={
                  rank != null
                    ? `Rank ${rank} of ${peers ?? '?'} ${ASCENDING.has(key) ? '(fewer days ranks higher)' : ''}`.trim()
                    : 'Not ranked — no value, or no peer has one'
                }
              >
                {rank != null ? `#${rank}${peers ? ` of ${peers}` : ''}` : 'unranked'}
              </span>
              <span className="truncate text-stone-600" title="Median across peers in this role">
                med {formatMeasure(peerMedian ?? null, key)}
              </span>
            </div>
            )}
            {figure.basis && (
              <div className="text-[8.5px] leading-snug text-stone-600">{figure.basis}</div>
            )}
            {key === 'quoteRatePct' && (
              <div
                className="truncate text-[8.5px] text-stone-600"
                title="Funded ÷ submitted — kept for reference; the desk's hit rate is the quote rate (T-71)"
              >
                funded ÷ submitted {formatMeasure(headline.measures.conversionPct, 'conversionPct')}
                {headline.rank?.conversionPct != null ? ` · #${headline.rank.conversionPct}` : ''}
              </div>
            )}
            {key === 'avgDaysToFund' && figure.value == null && (
              <div className="text-[8.5px] leading-snug text-stone-600">
                <BasisNote full="Received → funded is measured only where a deal was received before it funded; the deal book stamps an imported funded deal's received date on its funded day.">
                  No deal is dated received before funded.
                </BasisNote>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
